import React, { useState, useEffect } from "react";
import { makeStyles } from "@material-ui/core/styles";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  CircularProgress,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Typography,
  Box,
  Link
} from "@material-ui/core";
import { CloudUpload as CloudUploadIcon } from "@material-ui/icons";
import * as XLSX from "xlsx";
import api from "../../services/api";
import { toast } from "react-toastify";

const useStyles = makeStyles((theme) => ({
  uploadInput: {
    display: "none",
  },
  dropZone: {
    border: "2px dashed #ccc",
    borderRadius: 4,
    padding: theme.spacing(4),
    textAlign: "center",
    cursor: "pointer",
    marginBottom: theme.spacing(2),
    marginTop: theme.spacing(2),
    "&:hover": {
        borderColor: theme.palette.primary.main,
        backgroundColor: "#f9f9f9"
    }
  },
  dropZoneContent: {
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      gap: theme.spacing(1),
      color: "#2576d2"
  }
}));

const ImportContacts = ({ open, onClose, onImport }) => {
  const classes = useStyles();
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [parsedContacts, setParsedContacts] = useState([]);
  const [labels, setLabels] = useState([]);
  const [selectedLabelId, setSelectedLabelId] = useState(""); // Single label based on screenshot "Aplicar etiqueta a todos"
  const [whatsapps, setWhatsapps] = useState([]);
  const [selectedWhatsappId, setSelectedWhatsappId] = useState("");
  const [tagFolders, setTagFolders] = useState([]);

  useEffect(() => {
    if (open) {
        (async () => {
        try {
            const { data } = await api.get('/labels');
            setLabels(data);
            
            const { data: whatsappData } = await api.get('/whatsapp');
            const activeWhatsapps = whatsappData.filter(w => w.status === 'CONNECTED');
            setWhatsapps(activeWhatsapps);

            const { data: settingsData } = await api.get('/settings');
            const tagFoldersSetting = settingsData.find(s => s.key === 'tagFolders');
            if (tagFoldersSetting && tagFoldersSetting.value) {
                setTagFolders(JSON.parse(tagFoldersSetting.value));
            }
        } catch (e) {
            toast.error('Error cargando etiquetas, conexiones o ajustes');
        }
        })();
    }
  }, [open]);

  const handleFileChange = (e) => {
    const f = e.target.files[0];
    if (!f) return;
    setFile(f);
    
    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const data = evt.target.result;
        const wb = XLSX.read(data, { type: 'array' });
        const ws = wb.Sheets[wb.SheetNames[0]];
        const json = XLSX.utils.sheet_to_json(ws, { defval: '' });
        
        // Map fields and clean numbers
        const mapped = json.map(row => {
          const rawNumber = String(row['Número de teléfono'] || row['Telefono'] || row['Teléfono'] || row['phone'] || row['Phone'] || '');
          const cleanNumber = rawNumber.replace(/\D/g, ''); // Remove non-numeric chars
          
          return {
            name: row['Nombres y apellidos'] || row['Nombre'] || row['name'] || row['Name'] || '',
            number: cleanNumber
          };
        }).filter(c => c.number);

        // Remove duplicated numbers
        const uniqueContacts = [];
        const seenNumbers = new Set();

        for (const contact of mapped) {
          if (!seenNumbers.has(contact.number)) {
            seenNumbers.add(contact.number);
            uniqueContacts.push(contact);
          }
        }

        setParsedContacts(uniqueContacts);
      } catch (err) {
        toast.error("Error al leer archivo");
      }
    };
    reader.readAsArrayBuffer(f);
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      // Pass selectedLabelId as array if backend expects array, or single
      // Backend (ImportContactsFromFileService) likely expects labelIds array
      const labelIds = selectedLabelId ? [selectedLabelId] : [];
      
      await api.post('/contacts/upload', {
        contacts: parsedContacts,
        labelIds: labelIds,
        whatsappId: selectedWhatsappId
      });
      toast.success("Importación finalizada. Los contactos duplicados fueron ignorados.");
      if (onImport) onImport();
      handleClose();
    } catch (err) {
      toast.error("Error al importar contactos");
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
      onClose();
      setFile(null);
      setParsedContacts([]);
      setSelectedLabelId("");
      setSelectedWhatsappId("");
  }

  const downloadTemplate = () => {
    // Definimos los encabezados exactos que lee el sistema
    const headers = ["Nombres y apellidos", "Número de teléfono"];
    
    // Agregamos un par de filas de ejemplo
    const data = [
      headers,
      ["Alejandro Rodriguez", "573138311638"],
      ["Juan Perez", "573001234567"]
    ];

    // Creamos la hoja de cálculo y el libro
    const ws = XLSX.utils.aoa_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Contactos");

    // Descargamos el archivo
    XLSX.writeFile(wb, "Plantilla_Importacion_Contactos.xlsx");
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Importar contactos</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="textSecondary" gutterBottom>
            Por favor, suba un archivo .xlsx o .csv con su base de contactos. Puede descargar una <Link href="#" onClick={(e) => { e.preventDefault(); downloadTemplate(); }}>Plantilla</Link> y ver reglas básicas de columnas.
        </Typography>

        <input
          accept=".xlsx, .xls, .csv"
          className={classes.uploadInput}
          id="contained-button-file"
          type="file"
          onChange={handleFileChange}
        />
        <label htmlFor="contained-button-file">
          <Box className={classes.dropZone}>
              <div className={classes.dropZoneContent}>
                  <CloudUploadIcon fontSize="large" />
                  <Typography variant="button" style={{ fontWeight: 'bold' }}>
                      {file ? "CAMBIAR ARCHIVO" : "CARGAR ARCHIVO"}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                      {file ? file.name : "Haz clic para seleccionar o arrastra y suelta el archivo"}
                  </Typography>
              </div>
          </Box>
        </label>
        
        {file && parsedContacts.length > 0 && (
          <Typography variant="caption" color="primary" display="block" gutterBottom>
            {parsedContacts.length} registros detectados
          </Typography>
        )}

        <FormControl variant="outlined" fullWidth margin="normal">
          <InputLabel id="import-connection-select">Asignar Conexión (Obligatorio)</InputLabel>
          <Select
            labelId="import-connection-select"
            value={selectedWhatsappId}
            onChange={(e) => setSelectedWhatsappId(e.target.value)}
            label="Asignar Conexión (Obligatorio)"
          >
            <MenuItem value="">
              <em>Ninguna</em>
            </MenuItem>
            {whatsapps.map((whatsapp) => (
              <MenuItem key={whatsapp.id} value={whatsapp.id}>
                {whatsapp.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl variant="outlined" fullWidth margin="normal">
          <InputLabel id="import-label-select">Aplicar etiqueta a todos (Obligatorio)</InputLabel>
          <Select
            labelId="import-label-select"
            value={selectedLabelId}
            onChange={(e) => setSelectedLabelId(e.target.value)}
            label="Aplicar etiqueta a todos (Obligatorio)"
          >
            <MenuItem value="">
              <em>Ninguna</em>
            </MenuItem>
            {labels
              .filter(label => {
                if (!selectedWhatsappId) return true;
                
                // Las de sistema siempre pasan
                if (label.isSystem === true) return true;
                
                // Si la etiqueta tiene un whatsappId directo asignado, validamos que coincida
                if (label.whatsappId) {
                  return label.whatsappId == selectedWhatsappId;
                }

                // Validar por carpeta: si la etiqueta pertenece a una carpeta, verificamos si esa carpeta es de la conexión actual
                if (label.folderId) {
                  const folder = tagFolders.find(f => f.id === label.folderId);
                  if (folder && folder.whatsappId) {
                    return folder.whatsappId == selectedWhatsappId;
                  }
                }
                
                // Si la etiqueta NO tiene whatsappId ni está en una carpeta con whatsappId de esta conexión, la ocultamos.
                return false;
              })
              .map((label) => (
              <MenuItem key={label.id} value={label.id}>
                {label.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} color="primary" style={{ color: '#2576d2' }} disabled={loading}>
          CERRAR
        </Button>
        <Button 
          onClick={handleSubmit} 
          variant="contained" 
          disabled={!file || parsedContacts.length === 0 || loading || !selectedWhatsappId || !selectedLabelId}
          style={{ 
              backgroundColor: (!file || parsedContacts.length === 0 || loading || !selectedWhatsappId || !selectedLabelId) ? 'rgba(0, 0, 0, 0.12)' : '#2576d2',
              color: (!file || parsedContacts.length === 0 || loading || !selectedWhatsappId || !selectedLabelId) ? 'rgba(0, 0, 0, 0.26)' : '#fff',
              boxShadow: 'none'
          }}
        >
          {loading ? <CircularProgress size={24} /> : "IMPORTAR CONTACTOS"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ImportContacts;
